import { useState } from 'react'
import {
  Modal,
  Form,
  Select,
  Button,
  Alert,
  message,
  Space,
  Typography,
  Input,
  Checkbox,
} from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { hospitalImportApi } from '@/api/hospital-import'
import type { ExportFilters } from '@/types/hospital-import'

interface HospitalExportDialogProps {
  open: boolean
  onClose: () => void
  availableStates?: string[]
}

const { Text } = Typography

export const HospitalExportDialog: React.FC<HospitalExportDialogProps> = ({
  open,
  onClose,
  availableStates = [],
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleReset = () => {
    form.resetFields()
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleExport = async () => {
    const values = form.getFieldsValue()
    const filters: ExportFilters = {}

    // Only include filters if they have values
    if (values.state) {
      filters.state = values.state
    }
    if (values.participant !== undefined && values.participant !== 'all') {
      filters.participant = values.participant === 'true'
    }
    if (values.search) {
      filters.search = values.search
    }
    if (values.includeId === true) {
      filters.includeId = true
    }

    setLoading(true)
    try {
      const blob = await hospitalImportApi.exportHospitals(filters)

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with timestamp and filters
      const timestamp = new Date().toISOString().split('T')[0] // YYYY-MM-DD
      let filename = `hospitals-${timestamp}`
      if (filters.state) {
        filename += `-${filters.state}`
      }
      if (filters.participant !== undefined) {
        filename += filters.participant ? '-participants' : '-non-participants'
      }
      if (filters.search) {
        // Sanitize search term for filename (replace spaces and special chars)
        const sanitizedSearch = filters.search.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
        filename += `-${sanitizedSearch}`
      }
      if (filters.includeId) {
        filename += '-with-id'
      }
      filename += '.csv'

      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      message.success(`Exported hospitals to ${filename}`)
      handleClose()
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error ||
            'Export failed'
          : 'Export failed'
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Export Hospitals"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={loading}
        >
          Export
        </Button>,
      ]}
      width={500}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Export Hospitals to CSV"
          description="Download hospital data in CSV format. You can filter by state, participant status, or search term. Optionally include the ID field (primary key) in the export."
          type="info"
          showIcon
        />

        <Form form={form} layout="vertical">
          <Form.Item name="state" label="Filter by State" help="Leave empty to export all states">
            <Select
              placeholder="Select a state"
              allowClear
              showSearch
              options={availableStates.map((state) => ({
                label: state,
                value: state,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="participant"
            label="Filter by Participant Status"
            help="Leave empty to export all hospitals"
          >
            <Select
              placeholder="Select participant status"
              allowClear
              options={[
                { label: 'All Hospitals', value: 'all' },
                { label: 'Participants Only', value: 'true' },
                { label: 'Non-Participants Only', value: 'false' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="search"
            label="Filter by Search"
            help="Search by hospital name, city, or address"
          >
            <Input placeholder="Enter search term" allowClear />
          </Form.Item>

          <Form.Item
            name="includeId"
            valuePropName="checked"
            help="Include the ID field (primary key) as the first column"
          >
            <Checkbox>Include ID field in export</Checkbox>
          </Form.Item>
        </Form>

        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Note: The exported CSV can be modified and re-imported. Existing hospitals (by CMSID)
            will be updated with new values.
          </Text>
        </div>
      </Space>
    </Modal>
  )
}
